/**
 * InvoiceFilters Component - T111
 *
 * Combined filter panel for invoice list with all filter controls
 */
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { GlassPanel, SearchInput } from '../ui';
import { StatusFilter } from './StatusFilter';
import { DateRangeFilter } from './DateRangeFilter';
import type { InvoiceStatus } from '../../hooks/useInvoices';
import type { ReactNode } from 'react';

export interface InvoiceFiltersValue {
  q: string;
  status: InvoiceStatus | 'all' | 'active' | 'open';
  dateFrom: string | undefined;
  dateTo: string | undefined;
}

interface InvoiceFiltersProps {
  value: InvoiceFiltersValue;
  onChange: (value: InvoiceFiltersValue) => void;
  showDateFilter?: boolean;
  /**
   * 006b P3 — Slot für weitere Filter-Optionen (z. B. Quartale-Toggle),
   * damit die Seite EINE Filter-Fläche hat statt Streu-Rows darunter.
   */
  trailing?: ReactNode;
}

export function InvoiceFilters({ value, onChange, showDateFilter = true, trailing }: InvoiceFiltersProps) {
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(
    value.dateFrom !== undefined || value.dateTo !== undefined
  );

  const handleSearchChange = (q: string) => {
    onChange({ ...value, q });
  };

  const handleStatusChange = (status: InvoiceStatus | 'all' | 'active' | 'open') => {
    onChange({ ...value, status });
  };

  const handleDateChange = (dateFrom: string | undefined, dateTo: string | undefined) => {
    onChange({ ...value, dateFrom, dateTo });
  };

  const hasActiveFilters =
    value.q ||
    (value.status !== 'all' && value.status !== 'active') ||
    value.dateFrom ||
    value.dateTo;

  return (
    <GlassPanel className="mb-6 sticky top-0 z-20 !bg-zinc-950/95">
      <div className="space-y-4">
        {/* Search and expand */}
        <div className="flex gap-2 lg:gap-4 items-start flex-col lg:flex-row">
          <div className="flex-1 w-full lg:min-w-[200px]">
            <SearchInput
              value={value.q}
              onChange={handleSearchChange}
              placeholder="Suche nach Rechnungsnummer oder Kunde…"
            />
          </div>

          {showDateFilter && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`${mobileExpanded ? 'flex' : 'hidden lg:flex'} items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors shrink-0 ${
                isExpanded || value.dateFrom || value.dateTo
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Zeitraum
              {(value.dateFrom || value.dateTo) && (
                <span className="w-2 h-2 rounded-full bg-blue-400" />
              )}
            </button>
          )}
          <button type="button" aria-expanded={mobileExpanded} onClick={() => setMobileExpanded((open) => !open)} className="lg:hidden text-sm text-white/70 px-3 py-2 rounded-lg border border-white/10">
            {mobileExpanded ? 'Filter einklappen' : 'Filter und Datumsbasis'}
          </button>
        </div>

        <div className={`${mobileExpanded ? 'block' : 'hidden lg:block'} space-y-4`}>
        {/* Status filter */}
        <StatusFilter value={value.status} onChange={handleStatusChange} />

        {/* 006b P3 — trailing Filter-Optionen (Quartale-Toggle) */}
        {trailing && (
          <div className="flex flex-wrap items-end justify-start gap-3 pt-2 border-t border-white/5">
            {trailing}
          </div>
        )}

        {/* Date range filter (expandable) */}
        <AnimatePresence initial={false}>
          {showDateFilter && isExpanded && (
            <motion.div
              key="date-range"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{ overflow: 'hidden' }}
            >
              <div className="pt-2 border-t border-white/5">
                <DateRangeFilter
                  dateFrom={value.dateFrom}
                  dateTo={value.dateTo}
                  onChange={handleDateChange}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>

        {/* Active filters summary */}
        {hasActiveFilters && (
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2 pt-2 border-t border-white/5">
            <span className="text-xs text-white/40 break-words">
              {[
                value.q && `Suche: "${value.q}"`,
                value.status !== 'all' && value.status !== 'active' && `Status: ${value.status}`,
                (value.dateFrom || value.dateTo) &&
                  `Zeitraum: ${value.dateFrom || '...'} – ${value.dateTo || '...'}`,
              ]
                .filter(Boolean)
                .join(' • ')}
            </span>
            <button
              onClick={() =>
                onChange({ q: '', status: 'active', dateFrom: undefined, dateTo: undefined })
              }
              className="text-xs text-white/40 hover:text-white transition-colors shrink-0"
            >
              Alle Filter zurücksetzen
            </button>
          </div>
        )}
      </div>
    </GlassPanel>
  );
}
